import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useSite } from "../../lib/site";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Img } from "./Common";
import { useRecaptcha } from "../../hooks/useRecaptcha";

export default function ServiceInquiryModal({ open, onClose, serviceTitle = "", serviceSlug = "" }) {
  const { site } = useSite();
  const profile = site?.profile || {};
  const general = site?.general || {};
  const photo = general.inquiry_photo || general.contact_photo || profile.photo || profile.blog_photo;
  const name = general.inquiry_name || profile.name || "Olgu Tansuğ";
  const subtitle = general.inquiry_subtitle || serviceTitle || "";
  const { getToken, enabled: captchaEnabled } = useRecaptcha("service_inquiry");

  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", kvkk: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setF({ name: "", email: "", phone: "", message: serviceTitle ? `${serviceTitle} hakkında bilgi almak istiyorum. ` : "", kvkk: false });
  }, [open, serviceTitle]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    if (open) document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) { toast.error("Lütfen zorunlu alanları doldurun"); return; }
    if (!f.kvkk) { toast.error("KVKK onayı gerekli"); return; }
    setBusy(true);
    try {
      const token = await getToken();
      await api.post("/public/forms", {
        type: "inquiry",
        name: f.name.trim(),
        email: f.email.trim(),
        phone: f.phone.trim(),
        message: f.message.trim(),
        service_slug: serviceSlug,
        kvkk: true,
        recaptcha_token: token,
      });
      toast.success("Mesajınız iletildi. En kısa sürede dönüş yapacağım.");
      onClose();
    } catch (er) { toast.error(errMsg(er)); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-testid="service-inquiry-modal">
      <button type="button" aria-label="Kapat" onClick={onClose} className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden grid sm:grid-cols-[180px_1fr]">
        {/* Left: photo */}
        <div className="hidden sm:block relative bg-stone-100">
          {photo ? (
            <Img src={photo} alt={name} className="absolute inset-0 w-full h-full object-cover object-top" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-s-primary/15 text-s-primary font-heading text-5xl">{name.slice(0, 1)}</div>
          )}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <p className="text-white font-semibold text-sm">{name}</p>
            {subtitle && <p className="text-white/70 text-xs mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {/* Right: form */}
        <div className="p-6 sm:p-7 relative">
          <button type="button" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-500" aria-label="Kapat" data-testid="inquiry-close"><X size={18} /></button>
          <h3 className="font-heading text-xl text-stone-900 mb-1">Birlikte Değerlendirelim</h3>
          <p className="text-xs text-stone-500 mb-5">{serviceTitle ? `${serviceTitle} konusunda size nasıl yardımcı olabilirim?` : "İhtiyacınızı yazın, kısa sürede dönüş yapayım."}</p>
          <form onSubmit={submit} className="space-y-3" data-testid="inquiry-form">
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ad Soyad *" required data-testid="inquiry-name" />
            <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="E-posta *" required data-testid="inquiry-email" />
            <Input type="tel" inputMode="tel" autoComplete="tel" pattern="[0-9+\s\-()]{6,}" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value.replace(/[^\d+\s()\-]/g, "").replace(/(?!^)\+/g, "") })} placeholder="Telefon" data-testid="inquiry-phone" />
            <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="İhtiyacınızı kısaca anlatın *" rows={4} required data-testid="inquiry-message" />
            <label className="flex items-start gap-2 text-xs text-stone-600 select-none cursor-pointer">
              <input type="checkbox" checked={f.kvkk} onChange={(e) => setF({ ...f, kvkk: e.target.checked })} className="mt-0.5" data-testid="inquiry-kvkk" />
              <span>Kişisel verilerimin iletişim amacıyla işlenmesini onaylıyorum.</span>
            </label>
            <Button type="submit" disabled={busy} className="w-full" data-testid="inquiry-submit">
              <Send size={14} className="mr-1.5" />{busy ? "Gönderiliyor…" : "Gönder"}
            </Button>
            {captchaEnabled && (
              <p className="text-[10px] text-stone-400 text-center">
                Bu form Google reCAPTCHA ile korunmaktadır. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Gizlilik</a> ·{" "}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Şartlar</a>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
